import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'

import { Logo, Icon } from '@scentregroup/ui'

import { Link as LinkType } from '../../../types/link'

import style from './index.scss'
import Nav from './nav/main'
import Account from './nav/account'
import Search from './search'
import W from './w.inline.svg'
import { WithNavigationMenu, Centre, Cart, LinkItem, User } from './types'
import { shop } from './data'
import { LinkContext } from './context'

export { default as useCart } from './use-cart'

const UniversalHeader: React.FC<{
  Link: LinkType
  centres?: Array<Centre>
  shop?: WithNavigationMenu
  centre?: Centre
  giftCards?: WithNavigationMenu
  title?: LinkItem
  cart?: Cart
  links?: { items: Array<LinkItem>; hideMore?: boolean }
  user?: User
}> = ({ Link, centres, centre, giftCards, title, cart, links, user }) => {
  const universalHeaderRef = useRef<HTMLDivElement>(null)

  const [isNav, setIsNav] = useState(false)
  const [isAccount, setIsAccount] = useState(false)

  useEffect(() => {
    const el = universalHeaderRef.current || document.createElement('div')

    // eslint-disable-next-line compat/compat
    const observer = new IntersectionObserver(
      ([e]) => el.classList.toggle(style.stuck, e.intersectionRatio < 1),
      { threshold: [1] }
    )

    observer.observe(el)

    return () => observer.disconnect()
  })

  return (
    <>
      <div
        ref={universalHeaderRef}
        className={classNames(style['universal-header'])}
      >
        <div className={classNames(style['header-container'])}>
          <div className={classNames(style['header-contents'])}>
            <Icon
              size="medium"
              type="hamburger"
              className={classNames(style.icon, style.link, style.desktop)}
              onClick={() => setIsNav(true)}
            />
            {title ? (
              <>
                <Link to="/" className={classNames(style.link, style.desktop)}>
                  <W />
                </Link>
                <Link
                  to="/"
                  className={classNames(
                    style.link,
                    style.mobile,
                    style.unstuck
                  )}
                >
                  <W />
                </Link>
                <Link to={title.url} className={classNames(style.link)}>
                  {title.label}
                </Link>
              </>
            ) : (
              <Link to="/" className={classNames(style.link)}>
                <Logo className={classNames(style.desktop)} />
                <Logo className={classNames(style.mobile, style.unstuck)} />
                <W className={classNames(style.mobile, style.stuck)} />
              </Link>
            )}
            <div className={classNames(style.icons)}>
              <div className={classNames(style.desktop)}>
                <Search className={classNames(style.search)} />
              </div>
              <Icon
                size="medium"
                type="search"
                className={classNames(style.icon, style.mobile, style.stuck)}
              />
              {user && user.firstName ? (
                <button
                  className={classNames(style.avatar, style.link)}
                  onClick={() => setIsAccount(true)}
                >
                  {user.firstName.charAt(0)}
                </button>
              ) : (
                <Icon
                  size="medium"
                  type="user"
                  className={classNames(style.icon, style.link)}
                  onClick={() => setIsAccount(true)}
                />
              )}
              {cart && (
                <Link
                  title="Cart"
                  to={cart.url}
                  className={classNames(style.link, style.icon, {
                    [style['cart-active']]: Boolean(cart.count),
                  })}
                >
                  <Icon
                    size="medium"
                    type="shopping-cart"
                    className={classNames(style.icon)}
                  />
                </Link>
              )}
              <Icon
                size="medium"
                type="hamburger"
                className={classNames(style.icon, style.link, style.mobile)}
                onClick={() => setIsNav(true)}
              />
            </div>
          </div>
        </div>
        {links && (
          <div className={classNames(style['links-container'], style.desktop)}>
            <div className={classNames(style['links-contents'])}>
              {links.items.map((item, i) => (
                <Link key={i} to={item.url} className={classNames(style.link)}>
                  {item.label}
                </Link>
              ))}
              {!links.hideMore && (
                <button
                  className={classNames(style.link)}
                  onClick={() => setIsNav(true)}
                >
                  More...
                </button>
              )}
            </div>
          </div>
        )}
      </div>
      <div className={classNames(style.search, style.mobile)}>
        <Search />
      </div>
      {isNav && (
        <LinkContext.Provider value={Link}>
          <Nav
            centres={centres}
            centre={centre}
            shop={shop}
            giftCards={giftCards}
            onClose={() => setIsNav(false)}
          />
        </LinkContext.Provider>
      )}
      {isAccount && (
        <LinkContext.Provider value={Link}>
          <Account user={user} onClose={() => setIsAccount(false)} />
        </LinkContext.Provider>
      )}
    </>
  )
}

export default UniversalHeader
